import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationTriangle } from "@fortawesome/pro-solid-svg-icons";

import Button from "ui/Button";
import Loading from "ui/Loading";
import Panel from "ui/Panel";
import PanelSection from "ui/PanelSection";
import ServerGroupForm, {
  emptyTagRule,
  enteredRules,
  includableGroups,
  MatchRule,
  ruleMatchedIds,
  selectableServers,
  toMatchRuleInput,
  validationError,
} from "features/Server/ServerGroupForm";
import { ServerGroupMembersDocument } from "features/Server/ServerGroupForm/gql/Query.members.generated";
import PreviewDiff from "features/Server/ServerGroupPreviewDiff";
import { type SelectableGroup } from "features/Server/ServerGroupForm/GroupPicker";
import { type SelectableServer } from "features/Server/ServerGroupForm/ServerPicker";
import { useServerGroupFormOptionsQuery } from "features/Server/ServerGroupForm/gql/Query.generated";
import { useServerGroupMembers } from "features/Server/ServerGroupForm/useServerGroupMembers";
import { useSetFlash, useSetNextFlashLocation } from "providers/WithFlashes";
import { WithCurrentOrganizationQuery } from "providers/WithCurrentOrganization/gql/Query.generated";
import { useRoutes } from "utils/routes";

import { useDeleteServerGroupMutation } from "features/Server/ServerGroupEdit/gql/Mutation.delete.generated";
import { useUpdateCustomServerGroupMutation } from "features/Server/ServerGroupEdit/gql/Mutation.update.generated";

type OrganizationType =
  WithCurrentOrganizationQuery["getCurrentUserDetails"]["organizations"][number];

type Props = {
  serverGroup: OrganizationType["serverGroups"][number];
  currentOrganization: OrganizationType;
};

const ServerGroupEdit = ({ serverGroup, currentOrganization }: Props) => {
  const { data, loading, error } = useServerGroupFormOptionsQuery({
    variables: { organizationSlug: currentOrganization.slug },
  });
  if (loading || error) {
    return <Loading error={!!error} />;
  }

  const saved = data.getServerGroups.find(
    (group) => group.id === serverGroup.id,
  );

  return (
    <ServerGroupEditContent
      serverGroup={serverGroup}
      currentOrganization={currentOrganization}
      servers={selectableServers(currentOrganization)}
      serverGroups={includableGroups(data.getServerGroups, serverGroup.id)}
      savedMemberServerIds={saved?.memberServerIds ?? []}
      savedMemberServerGroupIds={saved?.memberServerGroupIds ?? []}
      canIncludeServerGroups={saved?.canIncludeServerGroups ?? true}
    />
  );
};

const ServerGroupEditContent = ({
  serverGroup,
  currentOrganization,
  servers,
  serverGroups,
  savedMemberServerIds,
  savedMemberServerGroupIds,
  canIncludeServerGroups,
}: Props & {
  servers: SelectableServer[];
  serverGroups: SelectableGroup[];
  savedMemberServerIds: string[];
  savedMemberServerGroupIds: string[];
  canIncludeServerGroups: boolean;
}) => {
  const navigate = useNavigate();
  const { organizationServerGroupsSettings } = useRoutes();
  const [name, setName] = useState(serverGroup.name);
  const [description, setDescription] = useState(serverGroup.description ?? "");
  const [matchRules, setMatchRules] = useState<MatchRule[]>(() =>
    serverGroup.matchRules.length > 0
      ? serverGroup.matchRules.map((rule) => ({
          type: rule.matchType,
          key: rule.matchKey ?? "",
          value: rule.pattern,
        }))
      : [emptyTagRule()],
  );
  const [memberServerIds, setMemberServerIds] = useState(savedMemberServerIds);
  const [memberServerGroupIds, setMemberServerGroupIds] = useState(
    savedMemberServerGroupIds,
  );

  const setFlash = useSetFlash();
  const setNextFlashLocation = useSetNextFlashLocation();
  const [updateServerGroup, { loading: updating }] =
    useUpdateCustomServerGroupMutation();
  const [deleteServerGroup, { loading: deleting }] =
    useDeleteServerGroupMutation();

  const candidateMatchRules = toMatchRuleInput(enteredRules(matchRules));
  const {
    baselineMembers,
    previewMembers,
    lastKnownPreviewMembers,
    loading: membersLoading,
    error: membersError,
    baselineVariables,
  } = useServerGroupMembers({
    organizationSlug: currentOrganization.slug,
    serverGroupId: serverGroup.id,
    matchRules: candidateMatchRules,
    memberServerIds,
    memberServerGroupIds,
  });
  // Holding the last answer while the next one loads keeps the locks from dropping and coming back
  const ruleMatchedServerIds = ruleMatchedIds(lastKnownPreviewMembers);

  const disabled = !serverGroup.permittedToEdit;
  const loading = updating || deleting;
  const trimmedName = name.trim();
  const trimmedDescription = description.trim();
  const invalidReason = disabled
    ? null
    : validationError({
        name,
        matchRules,
        memberServerIds,
        memberServerGroupIds,
      });
  const saveDisabled = disabled || loading || invalidReason != null;

  const handleSave = () => {
    // validate_membership! rejects a rule-matched server as an explicit member, and its checkbox is
    // locked, so keeping it would leave a save that can never succeed. Filter on a settled answer
    // only: acting on a stale one would silently drop a member, where sending it just fails visibly.
    const ruleMatched = new Set(ruleMatchedIds(previewMembers));
    updateServerGroup({
      variables: {
        serverGroupId: serverGroup.id,
        name: trimmedName,
        description: trimmedDescription === "" ? null : trimmedDescription,
        matchRules: candidateMatchRules,
        memberServerIds: memberServerIds.filter((id) => !ruleMatched.has(id)),
        memberServerGroupIds,
      },
      refetchQueries: [
        { query: ServerGroupMembersDocument, variables: baselineVariables },
        "ServerGroupFormOptions",
        "WithCurrentOrganization",
      ],
      awaitRefetchQueries: true,
      onError: (error) => {
        setFlash("error", `Failed to update server group: ${error.message}`);
      },
      onCompleted: (data) => {
        const errors = data.updateServerGroup.errors;
        if (errors && errors.length > 0) {
          setFlash("error", errors.join(", "));
          return;
        }
        setFlash("success", "Server group updated");
      },
    });
  };

  const handleCancel = () => {
    navigate(organizationServerGroupsSettings(currentOrganization.slug));
  };

  const handleDelete = () => {
    deleteServerGroup({
      variables: { serverGroupId: serverGroup.id },
      refetchQueries: ["WithCurrentOrganization", "ServerGroupFormOptions"],
      awaitRefetchQueries: true,
      onError: (error) => {
        setFlash("error", `Failed to delete server group: ${error.message}`);
      },
      onCompleted: () => {
        setFlash("success", "Server group deleted");
        setNextFlashLocation(
          organizationServerGroupsSettings(currentOrganization.slug),
        );
        navigate(organizationServerGroupsSettings(currentOrganization.slug));
      },
    });
  };

  return (
    <div className="flex items-start gap-4">
      <div className="flex-1 min-w-0">
        <ServerGroupForm
          name={name}
          description={description}
          matchRules={matchRules}
          servers={servers}
          serverGroups={serverGroups}
          memberServerIds={memberServerIds}
          memberServerGroupIds={memberServerGroupIds}
          ruleMatchedServerIds={ruleMatchedServerIds}
          canIncludeServerGroups={canIncludeServerGroups}
          disabled={disabled}
          onNameChange={setName}
          onDescriptionChange={setDescription}
          onMatchRulesChange={setMatchRules}
          onMemberServerIdsChange={setMemberServerIds}
          onMemberServerGroupIdsChange={setMemberServerGroupIds}
          footer={
            <Panel title="Save server group">
              <PanelSection className="flex items-center gap-2">
                <Button
                  bare
                  onClick={handleSave}
                  className="btn btn-success"
                  disabled={saveDisabled}
                  title={invalidReason ?? undefined}
                >
                  Save
                </Button>
                <Button bare onClick={handleCancel} variant="link">
                  Cancel
                </Button>
              </PanelSection>
            </Panel>
          }
        />
        <Panel title="Delete server group">
          {currentOrganization.permittedToDeleteServerGroups && (
            <PanelSection>
              <p>
                <FontAwesomeIcon
                  icon={faExclamationTriangle}
                  className="mr-2"
                />
                Deleting this server group will remove it from your organization
                and you will need to re-create it if you want to use it again.
              </p>
              <p>
                <Button
                  bare
                  onClick={handleDelete}
                  className="btn btn-danger"
                  disabled={loading}
                >
                  Delete Server Group
                </Button>
              </p>
            </PanelSection>
          )}
          {!currentOrganization.permittedToDeleteServerGroups && (
            <PanelSection>
              You are not allowed to delete this server group - you do not have
              the <strong>Manage</strong> permission on this organization.
            </PanelSection>
          )}
        </Panel>
      </div>
      <div className="w-[390px] shrink-0">
        <PreviewDiff
          baselineMembers={baselineMembers}
          previewMembers={previewMembers}
          loading={membersLoading}
          error={membersError}
        />
      </div>
    </div>
  );
};

export default ServerGroupEdit;
